/**
 * ES — Product Image Gallery
 * Custom element: <es-image-gallery>
 *
 * Features:
 * - Scroll-snap based main image slider (native swipe on mobile)
 * - Mouse drag-to-scroll on desktop
 * - Prev/next arrow navigation
 * - Thumbnail strip with active state sync
 * - Keyboard navigation (arrow keys)
 * - Lightbox integration (opens product-modal on image click)
 * - Variant switching via setActiveMedia() for product-info.js compatibility
 */
if (!customElements.get('es-image-gallery')) {
  customElements.define(
    'es-image-gallery',
    class EsImageGallery extends HTMLElement {
      connectedCallback() {
        // Defer init to ensure all children are parsed
        if (this._initialized) return;
        this._initialized = true;

        this.viewport = this.querySelector('.es-gallery__track');
        this.slides = [...this.querySelectorAll('.es-gallery__slide')];
        this.thumbs = [...this.querySelectorAll('.es-gallery__thumb')];
        this.prevBtn = this.querySelector('.es-gallery__arrow--prev');
        this.nextBtn = this.querySelector('.es-gallery__arrow--next');
        this.counterCurrent = this.querySelector('.es-gallery__counter-current');
        this.counterTotal = this.querySelector('.es-gallery__counter-total');

        this.currentIndex = 0;
        this.totalSlides = this.slides.length;
        this.scrollEndTimer = null;
        this.isScrolling = false;
        this.didDrag = false;
        this.isNavigating = false; // Flag: programmatic scroll in progress

        if (this.totalSlides <= 1) return;

        this.bindEvents();
        this.setupDrag();
        this.updateArrows();
      }

      bindEvents() {
        // Arrow navigation
        this.prevBtn?.addEventListener('click', () => this.goTo(this.currentIndex - 1));
        this.nextBtn?.addEventListener('click', () => this.goTo(this.currentIndex + 1));

        // Thumbnail clicks — use array index (matches updateState which highlights by position)
        this.thumbs.forEach((thumb, i) => {
          thumb.addEventListener('click', () => {
            this.goTo(i);
          });
        });

        // Detect active slide after scroll (touch swipe or mouse drag)
        this.viewport.addEventListener(
          'scroll',
          () => {
            this.isScrolling = true;
            clearTimeout(this.scrollEndTimer);
            this.scrollEndTimer = setTimeout(() => {
              this.isScrolling = false;
              // Only detect slide from scroll position during user-initiated scrolls,
              // not during programmatic navigation (arrows, thumbnails, variant switch)
              if (!this.isNavigating) {
                this.detectActiveSlide();
              }
              this.isNavigating = false;
            }, 120);
          },
          { passive: true }
        );

        // Keyboard navigation
        this.addEventListener('keydown', (e) => {
          if (e.key === 'ArrowLeft') {
            e.preventDefault();
            this.goTo(this.currentIndex - 1);
          }
          if (e.key === 'ArrowRight') {
            e.preventDefault();
            this.goTo(this.currentIndex + 1);
          }
        });

        // Prevent native image dragging (interferes with swipe)
        this.viewport.addEventListener('dragstart', (e) => e.preventDefault());
      }

      /**
       * Mouse drag-to-scroll — enables click-and-drag swiping on desktop
       */
      setupDrag() {
        let isDown = false;
        let startX = 0;
        let scrollStart = 0;

        this.viewport.addEventListener('mousedown', (e) => {
          if (e.button !== 0) return;
          isDown = true;
          this.didDrag = false;
          startX = e.clientX;
          scrollStart = this.viewport.scrollLeft;
          this.viewport.style.scrollBehavior = 'auto';
          this.viewport.style.scrollSnapType = 'none';
          this.viewport.classList.add('is-dragging');
        });

        window.addEventListener('mousemove', (e) => {
          if (!isDown) return;
          const dx = e.clientX - startX;
          if (Math.abs(dx) > 5) {
            this.didDrag = true;
            e.preventDefault();
          }
          this.viewport.scrollLeft = scrollStart - dx;
        });

        window.addEventListener('mouseup', () => {
          if (!isDown) return;
          isDown = false;
          this.viewport.style.scrollBehavior = '';
          this.viewport.style.scrollSnapType = '';
          this.viewport.classList.remove('is-dragging');

          if (this.didDrag) {
            this.snapToNearest();
            setTimeout(() => {
              this.didDrag = false;
            }, 50);
          }
        });
      }

      /**
       * After mouse drag ends, snap to the nearest slide
       */
      snapToNearest() {
        const index = this.getClosestSlideIndex();
        this.goTo(index);
      }

      /**
       * Navigate to a specific slide by index
       */
      goTo(index) {
        if (index < 0 || index >= this.totalSlides) return;
        this.currentIndex = index;
        this.isNavigating = true;

        const slide = this.slides[index];
        this.viewport.scrollTo({
          left: slide.offsetLeft,
          behavior: 'smooth',
        });

        this.updateState();
      }

      /**
       * Determine which slide is closest to the center of the viewport
       */
      getClosestSlideIndex() {
        const scrollLeft = this.viewport.scrollLeft;
        const viewportWidth = this.viewport.offsetWidth;
        const center = scrollLeft + viewportWidth / 2;

        let closestIndex = 0;
        let closestDistance = Infinity;

        this.slides.forEach((slide, i) => {
          const slideCenter = slide.offsetLeft + slide.offsetWidth / 2;
          const distance = Math.abs(slideCenter - center);
          if (distance < closestDistance) {
            closestDistance = distance;
            closestIndex = i;
          }
        });

        return closestIndex;
      }

      /**
       * Called after user-initiated scroll ends (touch swipe, native scroll)
       * NOT called after programmatic navigation (goTo, arrows, thumbnails)
       */
      detectActiveSlide() {
        const closestIndex = this.getClosestSlideIndex();
        if (closestIndex !== this.currentIndex) {
          this.currentIndex = closestIndex;
          this.updateState();
        }
      }

      /**
       * Sync all UI: slides, thumbnails, arrows, counter
       */
      updateState() {
        this.slides.forEach((slide, i) => {
          slide.classList.toggle('is-active', i === this.currentIndex);
        });

        this.thumbs.forEach((thumb, i) => {
          const isActive = i === this.currentIndex;
          thumb.classList.toggle('is-active', isActive);
          if (isActive) {
            thumb.setAttribute('aria-current', 'true');
          } else {
            thumb.removeAttribute('aria-current');
          }
        });

        this.scrollThumbIntoView();
        this.updateArrows();

        if (this.counterCurrent) {
          this.counterCurrent.textContent = this.currentIndex + 1;
        }
      }

      /**
       * Ensure the active thumbnail is visible in the scrollable container
       */
      scrollThumbIntoView() {
        const activeThumb = this.thumbs[this.currentIndex];
        if (!activeThumb) return;

        const container = activeThumb.parentElement;
        const thumbLeft = activeThumb.offsetLeft;
        const thumbWidth = activeThumb.offsetWidth;
        const containerScroll = container.scrollLeft;
        const containerWidth = container.offsetWidth;

        if (
          thumbLeft < containerScroll ||
          thumbLeft + thumbWidth > containerScroll + containerWidth
        ) {
          container.scrollTo({
            left: thumbLeft - containerWidth / 2 + thumbWidth / 2,
            behavior: 'smooth',
          });
        }
      }

      updateArrows() {
        if (this.prevBtn) this.prevBtn.disabled = this.currentIndex === 0;
        if (this.nextBtn) this.nextBtn.disabled = this.currentIndex === this.totalSlides - 1;
      }

      /**
       * API for product-info.js variant switching.
       * @param {string} mediaId - Format: "sectionId-mediaId"
       * @param {boolean} prepend - If true, move the media to the first position
       */
      setActiveMedia(mediaId, prepend) {
        const targetSlide = this.querySelector(`[data-media-id="${mediaId}"]`);
        if (!targetSlide) return;

        if (prepend) {
          const track = this.viewport;
          if (track.firstChild !== targetSlide) {
            track.prepend(targetSlide);
          }

          const targetThumb = this.querySelector(`.es-gallery__thumb[data-target="${mediaId}"]`);
          const thumbContainer = this.querySelector('.es-gallery__thumbs');
          if (targetThumb && thumbContainer && thumbContainer.firstChild !== targetThumb) {
            thumbContainer.prepend(targetThumb);
          }

          this.slides = [...this.querySelectorAll('.es-gallery__slide')];
          this.thumbs = [...this.querySelectorAll('.es-gallery__thumb')];
          this.totalSlides = this.slides.length;

          this.slides.forEach((slide, i) => (slide.dataset.index = i));
          this.thumbs.forEach((thumb, i) => (thumb.dataset.index = i));

          if (this.counterTotal) {
            this.counterTotal.textContent = this.totalSlides;
          }
        }

        const newIndex = this.slides.indexOf(targetSlide);
        if (newIndex === -1) return;

        this.currentIndex = newIndex;
        this.isNavigating = true;
        this.viewport.scrollTo({ left: targetSlide.offsetLeft, behavior: 'instant' });
        this.updateState();
      }
    }
  );
}
